import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChefHat, Trophy, ArrowLeft, Fire, Snowflake } from "@phosphor-icons/react";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import { useBulkSelect, SelectModeButton, BulkRecipeGrid, BulkMoveBar, withoutIds, withTags } from "@/components/BulkMove";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Public cook profile — /cook/:userId
 *
 * Read-only social identity page. Shows the cook's level, XP progress bar,
 * earned badges + the badges they still have to unlock, and their most
 * recent public recipes. Anyone (including anon) can view this page.
 *
 * The "still to unlock" catalog is filtered against the earned list so
 * displayed placeholders show the exact next things they can chase.
 */
export default function CookProfile() {
  const { userId } = useParams();
  const { flags, loading: flagsLoading } = useSiteFlags();
  const { user } = useAuth();
  const isStaff = ["Admin", "SuperAdmin"].includes(user?.role);
  const [profile, setProfile] = useState(null);
  const [catalog, setCatalog] = useState({ badges: [], levels: [] });
  const [recipes, setRecipes] = useState([]);
  const sel = useBulkSelect();
  const mine = (r) => !!user && r.owner_id === user.user_id;
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!userId || flagsLoading) return;
    if (!flags.gamification_enabled && !isStaff) return;
    setLoading(true);
    setNotFound(false);
    Promise.all([
      api.get(`/gamification/profile/${userId}`),
      api.get("/gamification/badges"),
    ])
      .then(([p, c]) => {
        setProfile(p.data);
        setCatalog(c.data);
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [userId, flags.gamification_enabled, flagsLoading, isStaff]);

  // Load the cook's public recipes for the "Their recipes" carousel — kept
  // separate so a failed leaderboard fetch still lets the rest render.
  useEffect(() => {
    if (!profile?.user_id) return;
    // Fetch broadly and filter client-side by owner_id since the directory
    // endpoint doesn't have an owner filter yet.
    api
      .get("/directory/recipes?limit=200")
      .then((r) => setRecipes((r.data || []).filter((rc) => rc.owner_id === profile.user_id).slice(0, 8)))
      .catch(() => setRecipes([]));
  }, [profile?.user_id]);

  if (!flagsLoading && !flags.gamification_enabled && !isStaff) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-center">
        <PageMeta title="Cook profiles coming soon" noindex />
        <ChefHat size={56} className="mx-auto text-rf-amber mb-4" weight="duotone" />
        <h1 className="font-display text-4xl font-black text-rf-ink mb-3">Coming soon.</h1>
        <p className="text-rf-ink-soft mb-6">Public cook profiles aren&apos;t open just yet.</p>
        <Link to="/directory" className="btn-amber">Browse the directory</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <p className="text-rf-ink-soft" data-testid="cook-profile-loading">Loading cook profile…</p>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 text-center">
        <PageMeta title="Cook not found" description="This cook profile is no longer available." />
        <ChefHat size={64} className="mx-auto text-rf-amber mb-4" weight="duotone" />
        <h1 className="font-display text-4xl font-black text-rf-ink mb-3">Cook not found</h1>
        <p className="text-rf-ink-soft mb-6">This cook&apos;s profile isn&apos;t available. They may have left the community.</p>
        <Link to="/directory" className="btn-amber">Browse the directory</Link>
      </div>
    );
  }

  const earnedIds = new Set((profile.badges || []).map((b) => b.id));
  const lockedBadges = (catalog.badges || []).filter((b) => !earnedIds.has(b.id));
  const level = profile.level || {};
  const displayName = profile.display_name || profile.username || "Anonymous Cook";
  const hasUsername = !!profile.username && !profile.username_suspended;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
      <PageMeta
        title={`${displayName} — Recipe Filer cook`}
        description={`${displayName} has shared ${profile.public_recipe_count} public recipes and earned ${profile.badges.length} badges on Recipe Filer. See their contributions.`}
      />

      <Link
        to="/leaderboard"
        data-testid="cook-profile-back"
        className="inline-flex items-center gap-2 text-sm font-bold text-rf-ink-soft hover:text-rf-orange mb-8"
      >
        <ArrowLeft size={16} /> Back to leaderboard
      </Link>

      {/* Hero card */}
      <div className="rounded-3xl bg-gradient-to-br from-rf-amber/30 to-rf-orange/10 border border-rf-amber/40 p-8 md:p-10 mb-12">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="w-28 h-28 rounded-full bg-white flex items-center justify-center font-black text-5xl text-rf-ink shadow-inner">
            {displayName.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <p className="overline mb-2">Recipe Filer Cook</p>
            <h1 className="font-display text-4xl md:text-6xl font-black text-rf-ink leading-[0.95] mb-3">
              {hasUsername ? <>@{displayName}</> : displayName}
            </h1>
            <p className="text-rf-ink-soft mb-6">
              Member since {profile.member_since} · {profile.public_recipe_count} public{" "}
              {profile.public_recipe_count === 1 ? "recipe" : "recipes"} · {profile.badges.length}{" "}
              {profile.badges.length === 1 ? "badge" : "badges"} earned
            </p>

            {/* Level + XP */}
            <div className="rounded-2xl bg-white/80 backdrop-blur border border-rf-ink/10 p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-rf-ink-soft font-bold">Level {level.level}</p>
                  <p className="font-display text-2xl font-black text-rf-ink leading-tight">{level.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider text-rf-ink-soft font-bold">XP</p>
                  <p className="font-display text-2xl font-black text-rf-orange leading-tight">{profile.xp}</p>
                </div>
              </div>
              <div className="w-full h-3 rounded-full bg-rf-ink/10 overflow-hidden">
                <div
                  data-testid="cook-profile-xp-bar"
                  className="h-full bg-gradient-to-r from-rf-amber to-rf-orange transition-all"
                  style={{ width: `${level.progress_pct || 0}%` }}
                />
              </div>
              {level.next_level_name ? (
                <p className="text-xs text-rf-ink-soft mt-2">
                  {level.next_level_at - profile.xp} XP to <b>{level.next_level_name}</b>
                </p>
              ) : (
                <p className="text-xs text-rf-orange font-bold mt-2 inline-flex items-center gap-1">
                  <Trophy size={12} weight="fill" /> Max level reached — Culinary Legend!
                </p>
              )}
            </div>

            {/* Streak card */}
            <div data-testid="cook-profile-streak-card" className="mt-4 rounded-2xl bg-white/80 backdrop-blur border border-rf-ink/10 p-5">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Fire size={16} weight={profile.current_streak > 0 ? "fill" : "regular"} className="text-rf-orange" />
                    <p className="text-xs uppercase tracking-wider text-rf-ink-soft font-bold">Current</p>
                  </div>
                  <p className="font-display text-2xl font-black text-rf-ink">
                    {profile.current_streak} <span className="text-sm font-bold text-rf-ink-soft">{profile.current_streak === 1 ? "day" : "days"}</span>
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Trophy size={16} weight="fill" className="text-rf-amber" />
                    <p className="text-xs uppercase tracking-wider text-rf-ink-soft font-bold">Longest</p>
                  </div>
                  <p className="font-display text-2xl font-black text-rf-ink">
                    {profile.longest_streak} <span className="text-sm font-bold text-rf-ink-soft">{profile.longest_streak === 1 ? "day" : "days"}</span>
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Snowflake size={16} weight="fill" className="text-sky-500" />
                    <p className="text-xs uppercase tracking-wider text-rf-ink-soft font-bold">Freezes</p>
                  </div>
                  <p className="font-display text-2xl font-black text-rf-ink">
                    {profile.streak_freezes}
                  </p>
                </div>
              </div>
              <p className="text-xs text-rf-ink-soft mt-3">
                Share a public recipe every day to grow your streak. Every 7-day streak earns you 1 <Snowflake size={12} weight="fill" className="inline text-sky-500" /> freeze that automatically saves your streak if you miss a day.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Level ladder — all 6 levels so users can see the whole climb */}
      {catalog.levels && catalog.levels.length > 0 && (
        <div className="mb-12" data-testid="cook-profile-level-ladder">
          <h2 className="font-display text-3xl font-black text-rf-ink mb-2">Level ladder</h2>
          <p className="text-sm text-rf-ink-soft mb-6">All the ranks a Recipe Filer cook can climb. XP earned by sharing public recipes.</p>
          <div className="rounded-3xl bg-white border border-rf-ink/10 p-6">
            <div className="flex flex-col md:flex-row gap-2 md:gap-0 md:items-stretch relative">
              {catalog.levels.map((lv, i) => {
                const reached = profile.xp >= lv.min_xp;
                const isCurrent = profile.level.level === lv.level;
                return (
                  <div
                    key={lv.level}
                    className={`flex-1 relative flex md:flex-col items-center gap-3 md:gap-0 md:text-center px-2 py-3 md:py-4 rounded-xl md:rounded-none ${isCurrent ? "bg-rf-amber/25 md:bg-transparent" : ""}`}
                  >
                    <div className={`shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center font-black text-sm md:text-base ${
                      isCurrent
                        ? "bg-rf-orange text-white ring-4 ring-rf-orange/25"
                        : reached
                          ? "bg-rf-amber text-rf-ink"
                          : "bg-rf-ink/5 text-rf-ink-soft"
                    }`}>
                      {lv.level}
                    </div>
                    <div className="flex-1 md:mt-2 min-w-0">
                      <p className={`text-xs md:text-sm font-black leading-tight ${reached ? "text-rf-ink" : "text-rf-ink-soft"} ${isCurrent ? "text-rf-orange" : ""}`}>
                        {lv.name}
                      </p>
                      <p className="text-[10px] uppercase tracking-wider text-rf-ink-soft font-bold mt-0.5">
                        {lv.min_xp} XP
                      </p>
                    </div>
                    {i < catalog.levels.length - 1 && (
                      <div className="hidden md:block absolute top-[38px] right-0 w-full h-0.5 bg-rf-ink/5 -z-0 translate-x-1/2" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Earned badges */}
      {profile.badges.length > 0 && (
        <div className="mb-12">
          <h2 className="font-display text-3xl font-black text-rf-ink mb-6">Badges earned</h2>
          <div data-testid="cook-profile-badges" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {profile.badges.map((b) => (
              <div
                key={b.id}
                className="rounded-2xl bg-white border border-rf-amber/40 p-4 flex items-start gap-3"
              >
                <div className="text-4xl leading-none">{b.emoji}</div>
                <div>
                  <p className="font-bold text-rf-ink text-sm">{b.name}</p>
                  <p className="text-xs text-rf-ink-soft mt-0.5">{b.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recipes */}
      {recipes.length > 0 && (
        <div className="mb-12">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="font-display text-3xl font-black text-rf-ink">Recent public recipes</h2>
            <SelectModeButton sel={sel} show={recipes.some(mine)} />
          </div>
          <BulkRecipeGrid recipes={recipes} sel={sel} canSelect={mine} testid="cook-profile-recipes" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" />
          <BulkMoveBar sel={sel} selectable={recipes.filter(mine)}
                       onTrashed={(res) => setRecipes(withoutIds(res))} onTagged={(res) => setRecipes(withTags(res))} />
        </div>
      )}

      {/* Locked badges */}
      {lockedBadges.length > 0 && (
        <div className="mb-12">
          <h2 className="font-display text-3xl font-black text-rf-ink mb-2">Badges to unlock</h2>
          <p className="text-sm text-rf-ink-soft mb-6">Keep sharing recipes to earn these next.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {lockedBadges.map((b) => (
              <div
                key={b.id}
                className="rounded-2xl bg-rf-ink/5 border border-dashed border-rf-ink/20 p-4 flex items-start gap-3 opacity-60"
              >
                <div className="text-4xl leading-none grayscale">{b.emoji}</div>
                <div>
                  <p className="font-bold text-rf-ink text-sm">{b.name}</p>
                  <p className="text-xs text-rf-ink-soft mt-0.5">{b.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
